import React, { useState, useRef } from "react";
import { View, StyleSheet, Pressable, GestureResponderEvent } from "react-native";
import { Menu } from "react-native-paper";
import { CaretDown } from "@phosphor-icons/react/dist/csr/CaretDown";

import CustomText from "../../../components/common/general/CustomText/CustomText";

interface AdminTablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
}

const AdminTablePagination: React.FC<AdminTablePaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [5, 10, 20, 50],
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef<View>(null);

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Pressable key={1} onPress={() => handlePageClick(1)} style={styles.pageButton}>
          <CustomText text="1" size="s" useTranslationText={false} style={styles.pageText} />
        </Pressable>
      );
      if (startPage > 2) {
        pages.push(
          <View key="dots1" style={styles.dots}>
            <CustomText text="..." size="s" useTranslationText={false} style={styles.pageText} />
          </View>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pressable
          key={i}
          onPress={() => handlePageClick(i)}
          style={[styles.pageButton, i === currentPage && styles.activePageButton]}>
          <CustomText
            text={i.toString()}
            size="s"
            useTranslationText={false}
            style={[styles.pageText, i === currentPage && styles.activePageText]}
          />
        </Pressable>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <View key="dots2" style={styles.dots}>
            <CustomText text="..." size="s" useTranslationText={false} style={styles.pageText} />
          </View>
        );
      }
      pages.push(
        <Pressable key={totalPages} onPress={() => handlePageClick(totalPages)} style={styles.pageButton}>
          <CustomText text={totalPages.toString()} size="s" useTranslationText={false} style={styles.pageText} />
        </Pressable>
      );
    }

    return pages;
  };

  const handleMenuOpen = (event: GestureResponderEvent) => {
    if (dropdownRef.current) {
      dropdownRef.current.measure((fx, fy, width, height, px, py) => {
        setMenuPosition({ x: px, y: py + height });
        setMenuVisible(true);
      });
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.leftSection}>
        <CustomText
          text={`Showing ${startItem}-${endItem} of ${totalItems.toLocaleString()}`}
          size="s"
          style={styles.infoText}
          useTranslationText={false}
        />
      </View>

      <View style={styles.centerSection}>
        <Pressable
          onPress={handlePreviousPage}
          disabled={currentPage === 1}
          style={[styles.navButton, currentPage === 1 && styles.disabledButton]}>
          <CustomText text="Previous" size="s" style={styles.navText} useTranslationText={false} />
        </Pressable>

        <View style={styles.pageNumbers}>{renderPageNumbers()}</View>

        <Pressable
          onPress={handleNextPage}
          disabled={currentPage === totalPages}
          style={[styles.navButton, currentPage === totalPages && styles.disabledButton]}>
          <CustomText text="Next" size="s" style={styles.navText} useTranslationText={false} />
        </Pressable>
      </View>

      <View style={styles.rightSection}>
        <CustomText text="Items per page:" size="s" style={styles.itemsPerPageLabel} />
        <Pressable ref={dropdownRef} onPress={handleMenuOpen} style={styles.itemsPerPageButton}>
          <CustomText
            text={itemsPerPage.toString()}
            size="s"
            useTranslationText={false}
            style={styles.itemsPerPageText}
          />
          <CaretDown size={16} />
        </Pressable>

        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={{ x: menuPosition.x, y: menuPosition.y }}
          contentStyle={styles.menu}>
          {itemsPerPageOptions.map((option) => (
            <Menu.Item
              key={option}
              onPress={() => {
                onItemsPerPageChange(option);
                setMenuVisible(false);
              }}
              title={option.toString()}
              titleStyle={styles.menuItemText}
            />
          ))}
        </Menu>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    backgroundColor: "#fff",
  },
  leftSection: {
    flex: 1,
  },
  centerSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rightSection: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },
  infoText: {
    color: "#6B7280",
  },
  navButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  disabledButton: {
    opacity: 0.5,
  },
  navText: {
    color: "#374151",
  },
  pageNumbers: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  pageButton: {
    minWidth: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
  },
  activePageButton: {
    backgroundColor: "#3B82F6",
  },
  pageText: {
    color: "#374151",
  },
  activePageText: {
    color: "#fff",
    fontWeight: "600",
  },
  dots: {
    paddingHorizontal: 4,
  },
  itemsPerPageLabel: {
    color: "#6B7280",
  },
  itemsPerPageButton: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 4,
  },
  itemsPerPageText: {
    color: "#374151",
  },
  menu: {
    backgroundColor: "#fff",
    marginTop: 4,
  },
  menuItemText: {
    fontSize: 14,
    color: "#374151",
  },
});

export default AdminTablePagination;
